import React, { useState } from "react";
import { DollarSign, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";

function formatCurrency(amount) {
  if (!amount && amount !== 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount);
}

export default function FECSection({ results, isSweeping, settingsHref = "/settings" }) {
  const [open, setOpen] = useState(true);

  const settingsPageLabel = /integration/i.test(String(settingsHref))
    ? "Settings → Integrations"
    : "Settings";

  const total = results.reduce((sum, r) => sum + (r.expenditure_amount || 0), 0);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-2 px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
        onClick={() => setOpen(v => !v)}
      >
        <DollarSign className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-semibold flex-1">FEC Filings</span>
        {isSweeping ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium">
            {results.length} results
          </span>
        )}
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="divide-y divide-border">
          <div className="px-4 py-2 bg-muted/20 text-[10px] text-muted-foreground italic">
            FEC filings reflect reported expenditures and may lag actual spend by 24 to 48 hours or more.
          </div>
          {results.length > 0 && (
            <div className="px-4 py-2 text-xs text-muted-foreground font-medium">
              Total reported expenditures this period: <span className="text-foreground font-semibold">{formatCurrency(total)}</span> across {results.length} filing{results.length !== 1 ? "s" : ""}
            </div>
          )}
          {results.length === 0 ? (
            <div className="px-4 py-4 space-y-2 text-sm text-muted-foreground">
              <p>No new results in the last 30 days.</p>
              <p className="text-xs">
                If you have not set an OpenFEC API key yet, add one under{" "}
                <Link to={settingsHref} className="text-primary underline hover:no-underline">
                  {settingsPageLabel}
                </Link>{" "}
                for higher rate limits (optional; the public demo key supports light use).
              </p>
            </div>
          ) : (
            results.map((r, i) => {
              const isHighValue = (r.expenditure_amount || 0) > 50000;
              const isOppose = r.support_oppose_indicator === "O";
              return (
                <div key={i} className={`px-4 py-3 space-y-1.5 ${isHighValue ? "border-l-4 border-l-red-500" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold">{r.committee_name}</span>
                    {isHighValue && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold shrink-0">High Value</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-foreground">{formatCurrency(r.expenditure_amount)}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isOppose ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                      {isOppose ? "Oppose" : "Support"}
                    </span>
                    {r.expenditure_description && <span className="text-xs text-muted-foreground">{r.expenditure_description}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    {r.expenditure_date && <span>{format(new Date(r.expenditure_date), "MMM d, yyyy")}</span>}
                    {r.filing_url && (
                      <a href={r.filing_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        View Filing →
                      </a>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}